(() => {
  if (customElements.get('cyco-silver-size-finder')) return;
  class SilverFinder extends HTMLElement {
    static fitMatches(wristCm, lengths) {
      const eps = 0.0000001;
      const sizes = [...new Set(lengths)].filter(Number.isFinite).sort((a,b) => a-b);
      if (!sizes.length || wristCm < 15.25 - eps || wristCm >= sizes[sizes.length - 1] - 0.1 - eps) return [];
      const fits = sizes.filter(length => length - wristCm > 0.1 + eps);
      const standard = fits.find(length => length - wristCm >= 1 - eps && length - wristCm <= 1.5 + eps);
      if (standard !== undefined) return [{ length: standard, fit: 'recommended' }];
      const first = fits[0];
      if (first === undefined) return [];
      if (first - wristCm < 1 - eps) {
        return [{ length: first, fit: 'closer' }, ...(fits[1] === undefined ? [] : [{ length: fits[1], fit: 'roomier' }])];
      }
      return [{ length: first, fit: 'roomier' }];
    }
    connectedCallback() {
      requestAnimationFrame(() => {
        if (!this.isConnected || this.initialized) return;
        this.initialized = true;
        this.labels = JSON.parse(this.querySelector('[data-silver-labels]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-silver-variants]').textContent);
        this.lengthIndex = Number(this.dataset.lengthIndex);
        this.units = 'cm';
        this.dialog = this.querySelector('[data-silver-dialog]');
        this.input = this.querySelector('[data-silver-wrist]');
        this.slider = this.querySelector('[data-silver-slider]');
        this.error = this.querySelector('[data-silver-error]');
        this.result = this.querySelector('[data-silver-result]');
        this.querySelector('[data-silver-open]').addEventListener('click', () => this.open());
        this.querySelector('[data-silver-close]').addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('close', () => {
          document.documentElement.style.overflow = this.previousOverflow || '';
          this.querySelector('[data-silver-open]').focus({ preventScroll: true });
        });
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        });
        this.querySelector('[data-silver-measure-form]').addEventListener('submit', event => {
          event.preventDefault();
          this.calculate();
        });
        this.querySelectorAll('[data-silver-unit]').forEach(button => button.addEventListener('click', () => this.setUnit(button.dataset.silverUnit)));
        this.input.addEventListener('input', () => {
          this.convertedValue = null;
          this.updateSlider(this.readWristCm());
          this.clearError();
          this.result.hidden = true;
        });
        this.slider.addEventListener('input', () => {
          const cm = Number(this.slider.value);
          this.input.value = String(this.units === 'in' ? Number((cm / 2.54).toFixed(2)) : cm);
          this.convertedValue = { cm, display: this.input.value };
          this.updateSlider(cm);
          this.clearError();
          this.result.hidden = true;
        });
        this.updateSlider(this.readWristCm());
      });
    }
    disconnectedCallback() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }
    text(key, values = {}) {
      let text = this.labels[key] || '';
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll('{' + name + '}', String(value)); });
      return text;
    }
    fmt(value) {
      return Number(value.toFixed(2)).toLocaleString(document.documentElement.lang || 'en', { maximumFractionDigits: 2 });
    }
    form() {
      return this.closest('.product-detail')?.querySelector('.js-qb-buy-buttons form');
    }
    picker() {
      return this.closest('.product-detail')?.querySelector('variant-picker');
    }
    selectedVariant() {
      const selectedId = this.form()?.querySelector('input[name="id"]')?.value || this.dataset.selectedId;
      return this.variants.find(variant => String(variant.id) === String(selectedId));
    }
    selectedPool() {
      const selected = this.selectedVariant();
      if (!selected) return [];
      return this.variants.filter(variant => variant.options.every((value, index) => index === this.lengthIndex || value === selected.options[index]));
    }
    open() {
      this.result.hidden = true;
      this.clearError();
      this.querySelector('[data-silver-selected]').hidden = true;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.input.focus();
    }
    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }
    showError(key) {
      this.error.textContent = this.text(key);
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }
    readWristCm() {
      if (this.convertedValue && this.input.value === this.convertedValue.display) return this.convertedValue.cm;
      return this.units === 'in' ? Number(this.input.value) * 2.54 : Number(this.input.value);
    }
    updateSlider(cm) {
      const valid = this.input.value.trim() && Number.isFinite(cm) && cm > 0;
      if (valid) this.slider.value = String(Math.min(26, Math.max(15, cm)));
      this.slider.setAttribute('aria-valuetext', this.fmt(Number(this.slider.value)) + ' cm');
      this.querySelector('[data-silver-slider-value]').textContent = valid ? this.fmt(cm) + ' cm (' + this.fmt(cm / 2.54) + ' in)' : '—';
      this.slider.style.setProperty('--silver-progress', ((Number(this.slider.value) - 15) / 11 * 100) + '%');
    }
    setUnit(next) {
      if (next === this.units) return;
      const cm = this.readWristCm();
      if (this.input.value.trim() && Number.isFinite(cm)) {
        this.input.value = String(next === 'in' ? Number((cm / 2.54).toFixed(2)) : Number(cm.toFixed(6)));
        this.convertedValue = { cm, display: this.input.value };
      }
      this.units = next;
      this.input.min = String(next === 'in' ? 15 / 2.54 : 15);
      this.input.max = String(next === 'in' ? 26 / 2.54 : 26);
      this.querySelectorAll('[data-silver-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.silverUnit === next)));
      this.querySelector('[data-silver-input-label]').textContent = this.text(next === 'in' ? 'measure_in' : 'measure_cm');
      this.querySelector('[data-silver-unit-suffix]').textContent = this.text(next === 'in' ? 'input_suffix_in' : 'input_suffix_cm');
      this.querySelector('[data-silver-unit-hint]').textContent = this.text(next === 'in' ? 'unit_hint_in' : 'unit_hint');
      this.updateSlider(cm);
      this.result.hidden = true;
      this.clearError();
    }
    calculate() {
      this.clearError();
      this.result.hidden = true;
      const value = Number(this.input.value);
      if (!this.input.value.trim() || !Number.isFinite(value) || value <= 0 || value > 100) { this.showError('invalid'); return; }
      this.wristCm = this.readWristCm();
      if (this.wristCm < 15 - 0.0000001 || this.wristCm > 26 + 0.0000001) { this.showError('invalid'); return; }
      const pool = this.selectedPool();
      const bands = SilverFinder.fitMatches(this.wristCm, pool.map(v => parseFloat(v.options[this.lengthIndex])).filter(Number.isFinite));
      const title = this.querySelector('[data-silver-result-title]');
      const note = this.querySelector('[data-silver-result-note]');
      const choices = this.querySelector('[data-silver-choices]');
      choices.replaceChildren();
      title.textContent = this.text(bands.length > 1 ? 'boundary_title' : bands.length ? 'result' : 'outside');
      note.textContent = this.text(bands.length > 1 ? 'boundary' : bands.length ? 'in_range' : 'outside_note', { wrist: this.fmt(this.wristCm), inch: this.fmt(this.wristCm / 2.54), sizes: [...new Set(pool.map(v => parseFloat(v.options[this.lengthIndex])).filter(Number.isFinite))].sort((a,b)=>a-b).map(n=>this.fmt(n)+' cm').join(', ') });
      this.querySelector('[data-silver-fit-note]').hidden = !bands.length;
      choices.classList.toggle('cyco-silver-finder__choices--single', bands.length === 1);
      const selected = this.selectedVariant();
      const otherOptions = selected ? selected.options.filter((_, index) => index !== this.lengthIndex).join(' / ') : '';
      this.querySelector('[data-silver-colour-note]').textContent = otherOptions ? this.text('colour', { colour: otherOptions }) : '';
      bands.forEach(band => {
        const card = document.createElement('div');
        card.className = 'cyco-silver-finder__choice';
        const badge = document.createElement('strong');
        badge.className = 'cyco-silver-finder__fit-label';
        badge.textContent = this.text(band.fit);
        badge.hidden = bands.length === 1 && band.fit === 'recommended';
        const heading = document.createElement('h4');
        heading.textContent = band.length + ' cm';
        const fit = document.createElement('p');
        fit.className = 'cyco-silver-finder__size-meta';
        fit.textContent = this.text('allowance', { room: this.fmt(band.length - this.wristCm), inch: this.fmt(band.length / 2.54) });
        card.append(badge, heading, fit);
        const variant = pool.find(item => parseFloat(String(item.options[this.lengthIndex])) === band.length && item.available);
        if (variant) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = this.text('apply', { length: band.length });
          button.addEventListener('click', () => this.apply(band, button));
          card.append(button);
        } else {
          const unavailable = document.createElement('p');
          unavailable.textContent = this.text('unavailable');
          card.append(unavailable);
        }
        choices.append(card);
      });
      this.result.hidden = false;
      title.focus({ preventScroll: true });
      this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    }
    async apply(band, button) {
      this.clearError();
      const variant = this.selectedPool().find(item => parseFloat(String(item.options[this.lengthIndex])) === band.length && item.available);
      if (!variant) { this.calculate(); this.showError('colour_changed'); return; }
      const picker = this.picker();
      const group = picker?.querySelector('.option-selector[data-index="' + (this.lengthIndex + 1) + '"]');
      if (!group) { this.showError('apply_error'); return; }
      const value = String(variant.options[this.lengthIndex]);
      const radio = [...group.querySelectorAll('.js-option')].find(element => String(element.value) === value);
      const select = group.querySelector('select');
      const customOption = [...group.querySelectorAll('.custom-select__option')].find(element => String(element.dataset.value || '').trim() === value);
      const colourBefore = this.selectedVariant()?.options.filter((_, index) => index !== this.lengthIndex).join(' / ');
      button.disabled = true;
      button.textContent = this.text('applying');
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (customOption) {
        customOption.click();
      } else {
        button.disabled = false;
        button.textContent = this.text('apply', { length: band.length });
        this.showError('apply_error');
        return;
      }
      let applied = false;
      for (let i = 0; i < 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        const selectedId = this.form()?.querySelector('input[name="id"]')?.value;
        if (String(selectedId) === String(variant.id)) { applied = true; break; }
      }
      button.disabled = false;
      button.textContent = this.text('apply', { length: band.length });
      if (!applied) { this.showError('apply_error'); return; }
      this.dialog.close();
      const status = this.querySelector('[data-silver-selected]');
      status.textContent = this.text('selected', { length: band.length, colour: colourBefore || '' });
      status.hidden = false;
      picker.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }
  }
  customElements.define('cyco-silver-size-finder', SilverFinder);
})();



